import { Badge, Box, Button, Heading, HStack, Spinner, Text, VStack, Wrap, WrapItem } from '@chakra-ui/react';
import { useGuideEntry, type GuideEntry, type GuideEntryKind } from '@/lib/sdk/ai-guide';
import { GuideMarkdown } from './GuideMarkdown';

const KIND_LABELS: Record<GuideEntryKind, string> = {
  capability: 'Feature',
  recipe: 'Recipe',
  concept: 'Concept',
  unsupported: 'Not supported',
};

interface GuideEntryViewProps {
  entryId: string | null;
  titles: Record<string, string>;
  onOpenEntry: (entryId: string) => void;
}

/** One guide entry in full, with links to related entries */
export function GuideEntryView({ entryId, titles, onOpenEntry }: GuideEntryViewProps) {
  const { data: entry, isLoading, isError } = useGuideEntry(entryId);

  if (!entryId) {
    return (
      <VStack align="start" spacing={2} data-testid="guide-empty">
        <Heading size="md">Workflow guide</Heading>
        <Text fontSize="sm" color="text-secondary">
          How to set up each workflow feature in the editor, with prerequisites and gotchas. Pick an entry, or ask a
          question on the right.
        </Text>
      </VStack>
    );
  }
  if (isLoading) {
    return <Spinner size="sm" />;
  }
  if (isError || !entry) {
    return (
      <Text fontSize="sm" color="text-secondary" data-testid="guide-entry-missing">
        This guide entry could not be loaded.
      </Text>
    );
  }
  return <EntryContent entry={entry} titles={titles} onOpenEntry={onOpenEntry} />;
}

function EntryContent({ entry, titles, onOpenEntry }: { entry: GuideEntry } & Omit<GuideEntryViewProps, 'entryId'>) {
  return (
    <VStack align="stretch" spacing={3}>
      <Box>
        <HStack spacing={2} mb={1}>
          <Heading size="md" data-testid="guide-entry-title">
            {entry.title}
          </Heading>
          <Badge colorScheme={entry.kind === 'unsupported' ? 'orange' : 'blue'}>{KIND_LABELS[entry.kind]}</Badge>
        </HStack>
        <Text fontSize="md" color="text-secondary">
          {entry.summary}
        </Text>
      </Box>

      <GuideMarkdown content={entry.body ?? ''} onOpenEntry={onOpenEntry} testId="guide-entry-body" />

      {(entry.related?.length ?? 0) > 0 && (
        <Box>
          <Text fontSize="xs" fontWeight="semibold" textTransform="uppercase" color="text-secondary" mb={1}>
            Related
          </Text>
          <Wrap spacing={2}>
            {entry.related?.map((relatedId) => (
              <WrapItem key={relatedId}>
                <Button size="xs" variant="outline" onClick={() => onOpenEntry(relatedId)} data-testid={`guide-related-${relatedId}`}>
                  {titles[relatedId] ?? relatedId}
                </Button>
              </WrapItem>
            ))}
          </Wrap>
        </Box>
      )}
    </VStack>
  );
}
